import * as React from "react"
import { ChevronLeft, ChevronRight, Calendar, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
]

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

/**
 * Calculate date range based on passenger type (PTC)
 * Based on Sabre/Airline standards:
 * - ADT: Adult (16+ years)
 * - CNN: Child (2 years – 11 years 11 months)
 * - YTH: Youth (12 years – 15 years 11 months)
 * - INF: Infant (0 – 1 year 11 months, no seat)
 */
const getDateRangeForPassengerType = (passengerType) => {
  const today = new Date()
  const type = (passengerType || 'ADULT').toUpperCase()
  
  let minDate, maxDate, defaultViewYear, ageRangeText
  
  switch (type) {
    case 'INFANT':
    case 'INF':
      // Infant: 0 – 1 year 11 months (must be under 2 on travel date)
      // Max DOB = today (just born)
      // Min DOB = 1 year 11 months ago
      maxDate = new Date(today)
      minDate = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate() + 1)
      defaultViewYear = today.getFullYear() - 1
      ageRangeText = 'Under 2 years'
      break
      
    case 'CHILD':
    case 'CHD':
    case 'CNN':
      // Child: 2 years – 11 years 11 months
      // Max DOB = 2 years ago (must be at least 2)
      // Min DOB = 12 years ago (must be under 12)
      maxDate = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate())
      minDate = new Date(today.getFullYear() - 12, today.getMonth(), today.getDate() + 1)
      defaultViewYear = today.getFullYear() - 7
      ageRangeText = '2 – 11 years'
      break
      
    case 'YOUTH':
    case 'YTH':
      // Youth: 12 years – 15 years 11 months
      // Max DOB = 12 years ago (must be at least 12)
      // Min DOB = 16 years ago (must be under 16)
      maxDate = new Date(today.getFullYear() - 12, today.getMonth(), today.getDate())
      minDate = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate() + 1)
      defaultViewYear = today.getFullYear() - 14
      ageRangeText = '12 – 15 years'
      break
      
    case 'ADULT':
    case 'ADT':
    default:
      // Adult: 16+ years
      // Max DOB = 16 years ago (must be at least 16)
      // Min DOB = 100 years ago (reasonable limit)
      maxDate = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate())
      minDate = new Date(today.getFullYear() - 100, 0, 1)
      defaultViewYear = today.getFullYear() - 30
      ageRangeText = '16+ years'
      break
  }
  
  return { minDate, maxDate, defaultViewYear, ageRangeText }
}

function DOBPicker({
  value,
  onChange,
  maxDate: propMaxDate,
  minDate: propMinDate,
  placeholder = "Select date of birth",
  className,
  passengerType = "ADULT",
  ...props
}) {
  // Calculate age-appropriate date range
  const dateRange = React.useMemo(() => 
    getDateRangeForPassengerType(passengerType), 
    [passengerType]
  )
  
  // Use calculated dates unless explicitly overridden by props
  const effectiveMinDate = propMinDate ? new Date(propMinDate) : dateRange.minDate
  const effectiveMaxDate = propMaxDate ? new Date(propMaxDate) : dateRange.maxDate
  
  const [isOpen, setIsOpen] = React.useState(false)
  const [viewDate, setViewDate] = React.useState(() => {
    if (value) return new Date(value)
    return new Date(dateRange.defaultViewYear, new Date().getMonth(), 1)
  })

  const selectedDate = value ? new Date(value) : null

  // Generate year range based on passenger type
  const years = React.useMemo(() => {
    const yrs = []
    const maxYear = effectiveMaxDate.getFullYear()
    const minYear = effectiveMinDate.getFullYear()
    for (let y = maxYear; y >= minYear; y--) {
      yrs.push(y)
    }
    return yrs
  }, [effectiveMinDate, effectiveMaxDate])

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate()
  }

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay()
  }

  // Generate calendar days
  const calendarDays = React.useMemo(() => {
    const year = viewDate.getFullYear()
    const month = viewDate.getMonth()
    const daysInMonth = getDaysInMonth(year, month)
    const firstDay = getFirstDayOfMonth(year, month)
    
    const days = []
    
    // Previous month days
    const prevMonthDays = getDaysInMonth(year, month - 1)
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i)
      })
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      })
    }
    
    // Next month days
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      })
    }
    
    return days
  }, [viewDate])

  // Check if date is disabled
  const isDateDisabled = (date) => {
    if (effectiveMaxDate && date > effectiveMaxDate) return true
    if (effectiveMinDate && date < effectiveMinDate) return true
    return false
  }

  // Check if date is selected
  const isDateSelected = (date) => {
    if (!selectedDate) return false
    return (
      date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate()
    )
  }

  // Check if date is today
  const isToday = (date) => {
    const today = new Date()
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    )
  }

  // Handle date selection
  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return
    const formattedDate = date.toISOString().split('T')[0]
    onChange(formattedDate)
    setIsOpen(false)
  }

  // Handle month change
  const handleMonthChange = (month) => {
    setViewDate(new Date(viewDate.getFullYear(), parseInt(month), 1))
  }

  // Handle year change
  const handleYearChange = (year) => {
    setViewDate(new Date(parseInt(year), viewDate.getMonth(), 1))
  }

  // Navigate months
  const goToPrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))
  }

  // Format display value
  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : null

  // Get passenger type label for display
  const getPassengerTypeLabel = () => {
    const type = (passengerType || 'ADULT').toUpperCase()
    switch (type) {
      case 'INFANT':
      case 'INF':
        return { label: 'Infant', color: 'text-pink-600 bg-pink-50 border-pink-200' }
      case 'CHILD':
      case 'CHD':
      case 'CNN':
        return { label: 'Child', color: 'text-blue-600 bg-blue-50 border-blue-200' }
      case 'YOUTH':
      case 'YTH':
        return { label: 'Youth', color: 'text-purple-600 bg-purple-50 border-purple-200' }
      default:
        return { label: 'Adult', color: 'text-slate-600 bg-slate-50 border-slate-200' }
    }
  }
  
  const typeInfo = getPassengerTypeLabel()

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full h-11 justify-start text-left font-normal text-sm overflow-hidden shadow-none",
            !value && "text-muted-foreground",
            className
          )}
          {...props}
        >
          <Calendar className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
          <span className="truncate">{displayValue || <span className="text-slate-400">{placeholder}</span>}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <div className="p-3">
          {/* Age Range Indicator */}
          <div className={cn(
            "mb-3 p-2 rounded-lg border text-center text-sm font-medium",
            typeInfo.color
          )}>
            <span>{typeInfo.label} DOB</span>
            <span className="mx-2">•</span>
            <span>{dateRange.ageRangeText}</span>
          </div>
          
          {/* Header with Month/Year Selectors */}
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevMonth}
              className="h-10 w-10"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Select
                value={viewDate.getMonth().toString()}
                onValueChange={handleMonthChange}
              >
                <SelectTrigger className="w-[130px] h-10 text-base font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, idx) => (
                    <SelectItem key={idx} value={idx.toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={viewDate.getFullYear().toString()}
                onValueChange={handleYearChange}
              >
                <SelectTrigger className="w-[100px] h-10 text-base font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextMonth}
              className="h-10 w-10"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((day) => (
              <div
                key={day}
                className="h-8 flex items-center justify-center text-xs font-semibold text-slate-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarDays.map((dayObj, idx) => {
              const disabled = isDateDisabled(dayObj.date)
              const selected = isDateSelected(dayObj.date)
              const today = isToday(dayObj.date)
              
              return (
                <button
                  key={idx}
                  onClick={() => handleDateSelect(dayObj.date)}
                  disabled={disabled || !dayObj.isCurrentMonth}
                  className={cn(
                    "h-9 w-full flex items-center justify-center rounded-md text-sm font-medium transition-colors",
                    !dayObj.isCurrentMonth && "text-slate-300",
                    dayObj.isCurrentMonth && !disabled && "hover:bg-brand-50 cursor-pointer",
                    disabled && dayObj.isCurrentMonth && "text-slate-300 cursor-not-allowed",
                    selected && "bg-brand-600 text-white hover:bg-brand-700",
                    today && !selected && "ring-2 ring-brand-500 ring-inset"
                  )}
                >
                  {dayObj.day}
                </button>
              )
            })}
          </div>

          {/* Quick Actions */}
          <div className="mt-3 pt-3 border-t flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange("")
                setIsOpen(false)
              }}
              className="text-slate-500"
            >
              Clear
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-brand-600"
            >
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

DOBPicker.displayName = "DOBPicker"

export { DOBPicker }
